import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import {
  useGetBusinessQuery,
  useGetBusinessStoreQuery,
  useGetStoreFeaturesQuery,
  useUpdateStoreFeaturesMutation,
} from "../../store/api";

const TOGGLE_FIELDS = [
  { name: "enablePos", label: "POS" },
  { name: "enableKiosk", label: "Kiosk" },
  { name: "enableKds", label: "KDS" },
  { name: "enableOrderDisplay", label: "Order Display" },
  { name: "enableQr", label: "QR" },
  { name: "enableOnline", label: "Online Ordering" },
  { name: "enablePayAtCounter", label: "Pay at Counter" },
  { name: "enableOnlinePayments", label: "Online Payments" },
  { name: "enablePhoneCapture", label: "Phone Capture" },
  { name: "showOutOfStock", label: "Show Out of Stock" },
  { name: "allowSpecialInstructions", label: "Special Instructions" },
  { name: "allowDineIn", label: "Dine In" },
  { name: "allowTakeaway", label: "Takeaway" },
  { name: "allowDelivery", label: "Delivery" },
];

const LIMIT_FIELDS = [
  { name: "maxKiosks", label: "Max Kiosks" },
  { name: "maxKds", label: "Max KDS" },
  { name: "maxOrderDisplays", label: "Max Order Displays" },
];

const POS_DINE_IN_MODES = [
  { value: "TABLE_DINE_IN", label: "Table Dine-In (with floor/tables)" },
  { value: "DINE_IN", label: "Dine-In (no tables)" },
  { value: "NONE", label: "None (disable dine-in)" },
];

const POS_FIELDS = [
  { name: "posDineInMode" },
  { name: "posEnableTakeaway" },
];

const normalizePosDineInMode = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (POS_DINE_IN_MODES.some((mode) => mode.value === raw)) {
    return raw;
  }
  return "TABLE_DINE_IN";
};

const createDefaultForm = () => ({
  ...Object.fromEntries(TOGGLE_FIELDS.map(({ name }) => [name, false])),
  ...Object.fromEntries(LIMIT_FIELDS.map(({ name }) => [name, ""])),
  posDineInMode: "TABLE_DINE_IN",
  posEnableTakeaway: true,
});

const mapFeaturesToForm = (features) => ({
  ...Object.fromEntries(
    TOGGLE_FIELDS.map(({ name }) => [name, Boolean(features?.[name])]),
  ),
  ...Object.fromEntries(
    LIMIT_FIELDS.map(({ name }) => [
      name,
      features?.[name] === null || features?.[name] === undefined
        ? ""
        : String(features[name]),
    ]),
  ),
  posDineInMode: normalizePosDineInMode(features?.posDineInMode),
  posEnableTakeaway:
    features?.posEnableTakeaway === null || features?.posEnableTakeaway === undefined
      ? true
      : Boolean(features.posEnableTakeaway),
});

const normalizeForPayload = (value) => ({
  ...Object.fromEntries(
    TOGGLE_FIELDS.map(({ name }) => [name, Boolean(value?.[name])]),
  ),
  ...Object.fromEntries(
    LIMIT_FIELDS.map(({ name }) => {
      const raw = (value?.[name] ?? "").toString().trim();
      return [name, raw === "" ? null : Number(raw)];
    }),
  ),
  posDineInMode: normalizePosDineInMode(value?.posDineInMode),
  posEnableTakeaway: Boolean(value?.posEnableTakeaway),
});

const getChangedPayload = (currentValue, initialValue) => {
  const current = normalizeForPayload(currentValue);
  const initial = normalizeForPayload(initialValue);
  const payload = {};

  [...TOGGLE_FIELDS, ...LIMIT_FIELDS, ...POS_FIELDS].forEach(({ name }) => {
    if (current[name] !== initial[name]) {
      payload[name] = current[name];
    }
  });

  return payload;
};

const validateLimits = (value) => {
  const nextErrors = {};
  LIMIT_FIELDS.forEach(({ name, label }) => {
    const raw = (value[name] ?? "").toString().trim();
    if (raw === "") return;

    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < 0) {
      nextErrors[name] = `${label} must be a whole number greater than or equal to 0.`;
    }
  });
  return nextErrors;
};

const validatePosFields = (value) => {
  const nextErrors = {};
  const raw = String(value?.posDineInMode || "").trim().toUpperCase();
  if (raw && !POS_DINE_IN_MODES.some((mode) => mode.value === raw)) {
    nextErrors.posDineInMode = "Select a valid POS dine-in mode.";
  }
  return nextErrors;
};

const StoreFeatures = () => {
  const { businessId, storeId } = useParams();
  const navigate = useNavigate();
  const isDraftStore = !storeId || storeId === "new";
  const { data: business } = useGetBusinessQuery(businessId);
  const { data: store } = useGetBusinessStoreQuery(
    { businessId, storeId },
    { skip: isDraftStore },
  );
  const {
    data: features,
    isLoading,
    isError,
    error,
  } = useGetStoreFeaturesQuery(storeId, { skip: isDraftStore });
  const [updateStoreFeatures, updateState] = useUpdateStoreFeaturesMutation();

  const [form, setForm] = useState(createDefaultForm);
  const [initialForm, setInitialForm] = useState(createDefaultForm);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (features) {
      const mapped = mapFeaturesToForm(features);
      setForm(mapped);
      setInitialForm(mapped);
      setErrors({});
    }
  }, [features]);

  const hasChanges = useMemo(
    () => Object.keys(getChangedPayload(form, initialForm)).length > 0,
    [form, initialForm],
  );

  const handleToggleChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
    setSuccessMessage("");
  };

  const handleLimitChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSuccessMessage("");
  };

  const handlePosModeChange = (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, posDineInMode: value }));
    if (errors.posDineInMode) {
      setErrors((prev) => ({ ...prev, posDineInMode: "" }));
    }
    setSuccessMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    if (isDraftStore) return;

    const validationErrors = {
      ...validateLimits(form),
      ...validatePosFields(form),
    };
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = getChangedPayload(form, initialForm);
    if (Object.keys(payload).length === 0) return;

    try {
      await updateStoreFeatures({ storeId, payload }).unwrap();
      setInitialForm(form);
      setSuccessMessage("Store features updated.");
    } catch (err) {
      // error handled by UI
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Store Features"
          subtitle="Loading store features..."
          actions={(
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          )}
        />
        <Card>
          <p className="text-sm text-slate-500">Loading store features...</p>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Store Features"
          subtitle="Error loading store features"
          actions={(
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          )}
        />
        <Card>
          <Alert tone="error">
            {error?.data?.detail || "Failed to load store features."}
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Store Features"
        subtitle={`${business?.businessName || ""} - ${store?.storeName || "Store"} feature configuration`}
        actions={(
          <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        )}
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section
            title="Feature Toggles"
            subtitle="Enable or disable store-level modules used by Edge apps."
          >
            <div className="grid gap-4 md:grid-cols-3">
              {TOGGLE_FIELDS.map((field) => (
                <Checkbox
                  key={field.name}
                  name={field.name}
                  checked={form[field.name]}
                  onChange={handleToggleChange}
                  label={field.label}
                />
              ))}
            </div>
          </Section>

          <Section
            title="POS Order Types"
            subtitle="Configure which order types are available in POS for this store."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="POS Dine-In Mode"
                name="posDineInMode"
                value={form.posDineInMode}
                onChange={handlePosModeChange}
                error={errors.posDineInMode}
                helper="Exactly one dine-in flow can be active for POS at a time."
              >
                {POS_DINE_IN_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </Select>
              <Checkbox
                name="posEnableTakeaway"
                checked={form.posEnableTakeaway}
                onChange={handleToggleChange}
                label="Enable POS Takeaway"
                helper="Enable or disable takeaway for POS."
              />
            </div>
          </Section>

          <Section
            title="Device Limits"
            subtitle="Clear a value to send null (unlimited)."
          >
            <div className="grid gap-4 md:grid-cols-3">
              {LIMIT_FIELDS.map((field) => (
                <Input
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  type="number"
                  min="0"
                  step="1"
                  value={form[field.name]}
                  onChange={handleLimitChange}
                  error={errors[field.name]}
                  placeholder="Leave empty for unlimited"
                />
              ))}
            </div>
          </Section>

          {successMessage && (
            <Alert tone="success">{successMessage}</Alert>
          )}
          {isDraftStore && (
            <Alert tone="warning">
              You are in draft mode. Create the store first to save store features.
            </Alert>
          )}
          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail || "Failed to update store features."}
            </Alert>
          )}

          <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 md:flex-row">
            <div className={isDraftStore ? "md:basis-[70%]" : "md:basis-full"}>
              <Button
                type="submit"
                className="w-full"
                disabled={isDraftStore || updateState.isLoading || !hasChanges}
              >
                {updateState.isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
            {isDraftStore && (
              <div className="md:basis-[30%]">
                <Link to={`/businesses/${businessId}/licenses/new`}>
                  <Button type="button" variant="secondary" className="w-full">
                    Allocate License
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default StoreFeatures;
